/**
 * ThreadView — a conversa interna aberta (pane direita de MensagensPage)
 * (Item #8b, Slice D).
 *
 * Cabeçalho (nome da contraparte / rótulo genérico do canal do time + badge
 * "Interna" com cadeado + chip de âncora) → timeline de bubbles → composer.
 *
 * Visual "casa, não cliente": bubbles MINHAS em --brand-subtle com borda âmbar
 * (NÃO o verde de conversa-com-cliente) + badge "Interna" com cadeado. Nomes de
 * remetente resolvidos pela presença, degradando honesto (lição do projeto).
 *
 * Marca como lido ao abrir (useEffect keyed no threadId) — zera o badge daquele
 * thread. Estados: loading, erro/indisponível (403 DM_NOT_PARTICIPANT /
 * DM_THREAD_NOT_FOUND), e vazio (mostra cabeçalho + composer).
 */

import { Link2, Lock } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { avatarColorFor, initialsOf } from '../../../lib/format';
import { useAuthStore } from '../../../stores/auth-store';
import { relativeTime } from '../../conversations/format';
import { useMarkRead, useReply, useThread } from '../hooks/use-direct-messages';
import { useMarkReadOnIncoming } from '../hooks/use-mark-read-on-incoming';
import {
  detailCounterpartName,
  groupParticipantsLabel,
  participantNameMap,
  senderDisplayName,
} from '../thread-display';

import { MessageComposer } from './MessageComposer';

import type { DirectMessage, ThreadDetail } from '../types';

export function ThreadView({ threadId }: { threadId: string }): JSX.Element {
  const { data: thread, isLoading, isError } = useThread(threadId);
  const markRead = useMarkRead();
  const reply = useReply(threadId);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  // Marca como lido UMA vez ao abrir / trocar de thread (zera o badge).
  // mutateRef evita re-disparo a cada render por mudança de identidade do mutate.
  const mutateReadRef = useRef(markRead.mutate);
  mutateReadRef.current = markRead.mutate;
  useEffect(() => {
    if (threadId.length === 0) return;
    mutateReadRef.current(threadId);
  }, [threadId]);

  // N2-14: mensagem nova de OUTRO participante enquanto a thread está ABERTA e
  // visível também marca como lida — o WS sync só invalida a query (a mensagem
  // aparece na timeline), e sem isto `lastReadAt` fica ANTERIOR ao que o operador
  // já leu na tela: a lista dizia «1 não lidas» com a conversa aberta ao lado.
  // Aba oculta: adia e dispara quando ela volta a ficar visível.
  useMarkReadOnIncoming(threadId, thread, currentUserId, mutateReadRef);

  if (isLoading) {
    return (
      <div className="dm-thread-view dm-thread-view--state">
        <span className="dm-thread-view__state-text" role="status">
          Abrindo conversa…
        </span>
      </div>
    );
  }

  if (isError || thread === undefined) {
    return (
      <div className="dm-thread-view dm-thread-view--state">
        <Lock size={28} strokeWidth={1.5} className="dm-thread-view__state-icon" aria-hidden />
        <span className="dm-thread-view__state-text">Esta conversa não está disponível.</span>
      </div>
    );
  }

  return (
    <ThreadViewLoaded
      thread={thread}
      onSend={(body) => {
        reply.mutate({ body });
      }}
      isSending={reply.isPending}
    />
  );
}

interface ThreadViewLoadedProps {
  thread: ThreadDetail;
  onSend: (body: string) => void;
  isSending: boolean;
}

function ThreadViewLoaded({ thread, onSend, isSending }: ThreadViewLoadedProps): JSX.Element {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const nameByUserId = participantNameMap(thread.participantNames);

  const isGroup = thread.kind === 'group';
  const title = isGroup
    ? groupParticipantsLabel(thread.participantUserIds.length)
    : detailCounterpartName(thread.participantUserIds, currentUserId, nameByUserId);

  // Auto-scroll para a mensagem mais nova ao carregar / após responder.
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const messageCount = thread.messages.length;
  useEffect(() => {
    const el = timelineRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [messageCount]);

  return (
    <div className="dm-thread-view">
      <header className="dm-thread-view__header">
        <div className="dm-thread-view__heading">
          <h2 className="dm-thread-view__title">{title}</h2>
          <span className="dm-thread-view__badge" title="Conversa interna entre operadores">
            <Lock size={11} strokeWidth={1.9} aria-hidden />
            Interna
          </span>
        </div>
        {thread.anchorConversationId !== null && (
          <Link
            to={`/atendimento/conversation/${thread.anchorConversationId}`}
            className="dm-chip dm-chip--anchor dm-thread-view__anchor"
          >
            <Link2 size={11} strokeWidth={1.75} aria-hidden />
            Sobre uma conversa
          </Link>
        )}
      </header>

      <div ref={timelineRef} className="dm-thread-view__timeline" aria-label="Mensagens">
        {thread.messages.length === 0 ? (
          <div className="dm-thread-view__empty">
            <span className="dm-thread-view__empty-text">
              Nenhuma mensagem ainda. Escreva a primeira abaixo.
            </span>
          </div>
        ) : (
          thread.messages.map((message) => (
            <MessageBubble
              key={message.messageId}
              message={message}
              isMine={message.senderUserId === currentUserId}
              isGroup={isGroup}
              nameByUserId={nameByUserId}
            />
          ))
        )}
      </div>

      <MessageComposer onSend={onSend} isSending={isSending} />
    </div>
  );
}

interface MessageBubbleProps {
  message: DirectMessage;
  isMine: boolean;
  isGroup: boolean;
  nameByUserId: Map<string, string>;
}

function MessageBubble({
  message,
  isMine,
  isGroup,
  nameByUserId,
}: MessageBubbleProps): JSX.Element {
  const senderName = senderDisplayName(message.senderUserId, nameByUserId);
  // Nome do remetente acima do bubble só faz sentido em grupo, para mensagens
  // dos outros (em 1:1 a contraparte já está no cabeçalho).
  const showSenderName = isGroup && !isMine;

  const rowClass = ['dm-msg', isMine ? 'dm-msg--mine' : 'dm-msg--other'].join(' ');

  return (
    <div className={rowClass} data-mine={isMine ? 'true' : 'false'}>
      {!isMine && (
        <span
          aria-hidden
          className="dm-msg__avatar"
          style={{ background: avatarColorFor(message.senderUserId) }}
        >
          {initialsOf(senderName)}
        </span>
      )}
      <div className="dm-msg__col">
        {showSenderName && <span className="dm-msg__sender">{senderName}</span>}
        <div className="dm-msg__bubble">{message.body}</div>
        <span className="dm-msg__time">{relativeTime(message.createdAt)}</span>
      </div>
    </div>
  );
}
